import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChannels } from '@connect-x-app/hybrid-state';
import { Text } from '@connect-x-app/hybrid-ui';
import { FlightRoomUser, MessageMention } from '@connect-x-app/shared-domain-models';
import { COLORS } from '@connect-x-app/shared-ui';
import { SideModal, SideModalHeader } from '@connect-x-app/web-ui';

import { CrewMemberInfoItem } from '../crewMemberInfoItem';
import { CrewProfile } from '../crewProfile';
import { ExternalMentionInfoItem } from '../externalMentionInfoItem';

type MentionInfoSideModalProps = {
  activeMention?: MessageMention | null;
  onClose: () => void;
  showOfflineText?: boolean;
};

type Screen = { name: 'MENTION_DETAILS'; user: FlightRoomUser } | { name: 'MENTION_INFO' };

const MENTION_INFO_SCREEN: Screen = { name: 'MENTION_INFO' };

export const MentionInfoSideModal: FC<MentionInfoSideModalProps> = ({ activeMention, onClose, showOfflineText }) => {
  const { selectedChannel } = useChannels();
  const { t } = useTranslation();

  const [screen, setScreen] = useState<Screen>(MENTION_INFO_SCREEN);

  useEffect(() => {
    if (!activeMention) {
      setScreen(MENTION_INFO_SCREEN);
    }
  }, [activeMention]);

  const getUsers = () => {
    const mention = activeMention;
    return mention && selectedChannel?.users?.filter(user => mention?.users?.includes(user.externalId));
  };

  const handleUserClick = (user: FlightRoomUser) => {
    setScreen({ name: 'MENTION_DETAILS', user });
  };

  const renderMentionInfo = () => {
    if (activeMention) {
      const mentionedUsers = getUsers();
      if (!mentionedUsers?.length) {
        return <ExternalMentionInfoItem mention={activeMention} />;
      }
      return renderInternalMention(mentionedUsers);
    }

    return undefined;
  };

  const renderInternalMention = (flightRoomUsers: FlightRoomUser[]) => {
    return (
      flightRoomUsers && (
        <>
          {flightRoomUsers?.map(crewMember => (
            <CrewMemberInfoItem crewMember={crewMember} key={crewMember.externalId} onClick={handleUserClick} />
          ))}
        </>
      )
    );
  };

  const renderContent = () => {
    if (showOfflineText) {
      return <Text>{t('OFFLINE.USERS')}</Text>;
    }

    switch (screen.name) {
      case 'MENTION_DETAILS':
        return <CrewProfile crewMember={screen.user} />;
      case 'MENTION_INFO':
      default:
        return renderMentionInfo();
    }
  };

  const renderHeader = (): SideModalHeader => {
    switch (screen.name) {
      case 'MENTION_DETAILS':
        return {
          backgroundColor: COLORS.victor.$03,
          leftIcon: {
            iconName: 'arrowLeft',
            onClick: () => setScreen(MENTION_INFO_SCREEN),
          },
          title: { props: { uppercase: false }, text: t('PROFILE.HEADER_TITLE') },
        };
      case 'MENTION_INFO':
      default:
        return {
          rightIcon: { iconName: 'closeCross', onClick: onClose },
          title: { props: { uppercase: false }, text: activeMention?.label ? `@${activeMention.label}` : '' },
        };
    }
  };

  return (
    <SideModal
      content={renderContent()}
      fullwidth={screen.name === 'MENTION_DETAILS'}
      header={renderHeader()}
      isVisible={!!activeMention}
      onBackgroundClick={onClose}
    />
  );
};
